import React, { useState, useEffect } from 'react'
import { useApolloClient, useLazyQuery } from '@apollo/client'
import {
  FileImageOutlined,
  LoadingOutlined,
  PoundOutlined,
  QuestionCircleOutlined
} from '@ant-design/icons'
import { Link } from 'react-router-dom'
import moment from 'moment-timezone'
import { uuid } from 'uuidv4'
import { useMutation } from '@apollo/client'
import { useSlerp } from '@slerp/client'
import {
  Avatar,
  Button,
  DatePicker,
  Form,
  Input,
  message,
  Switch,
  Row,
  Col,
  Upload,
  Tabs,
  Radio,
  Space,
  Tooltip
} from 'antd'
import {
  UPSERT_LOYALTY_CARD_REWARDS,
  ARCHIVE_REWARD,
  UPLOAD_LOYALTY_CARD_IMAGE,
  UNREDEEM_CUSTOMER_LOYALTY_CARDS,
  SET_CUSTOMER_REWARDS_EXPIRATION,
  GET_ACTIVE_MERCHANT_CATEGORIES,
  BULK_ADD_CUSTOMERS_TO_EXCLUSIVE_CARD,
  GENERATE_STAMP_QR_TOKEN
} from './LoyaltyQueries'
import { intersection } from 'lodash'
import { LoyaltyCardType, LoyaltyRewardType, StoreType, User } from './types'
import {
  startDateRule,
  requiredRule,
  requiredImageRule,
  nonZeroRule,
  nonNegativeRule,
  uniqueName,
  reqProductIdsRule
} from './Rules'
import hash from 'object-hash'
import { TextFieldNumber } from './../Widgets'
import env from '../../env'
import RewardsForm from './RewardsForm'
import ProductPanels from './ProductPanels'
import LoyaltyStoreSelect from './LoyaltyStoreSelect'
import { stringListForMutation } from './utils'
import ExclusiveForm from './ExclusiveForm'

const { useForm } = Form
const { TextArea } = Input
const { TabPane } = Tabs
export interface Category {
  id: string
  name: string
  products: {
    name: string
  }[]
}

interface BulkAddResponse {
  data?: {
    bulkAddCustomersExclusiveLoyaltyCard: string
  }
}

interface LoyaltyCardFormProps {
  activeCard: LoyaltyCardType | null
  activeRewards: LoyaltyRewardType[]
  stores: StoreType[]
  onDrawerClose: () => void
}

const LoyaltyCardForm = (props: LoyaltyCardFormProps) => {
  const { activeCard, activeRewards, onDrawerClose, stores } = props
  const [form] = useForm()

  const cardProfile = activeCard
  const rewardsProfile =
    activeRewards?.sort(
      (a, b) => new Date(a.inserted_at) - new Date(b.inserted_at)
    ) || []
  const action = cardProfile ? 'update' : 'create'
  const shouldQueryOnRewards = () => {
    return rewardsProfile.some((reward) => reward?.reward_type === 'product')
  }

  const { user } = useSlerp()
  const client = useApolloClient()
  const codeHashed = hash.MD5(`${uuid()}${moment(Date.now()).unix()}`)

  const [loyaltyCardId, setLoyaltyCardId] = useState(cardProfile?.id || uuid())
  const [customerIds, setCustomerIds] = useState<string[]>([])
  const [activeTab, setActiveTab] = useState('loyalty-card')
  const [disabledSubmit, setDisabledSubmit] = useState(false)
  const [image, setImage] = useState()
  const [imageUploadLoading, setImageUploadLoading] = useState(false)
  const [imageUrl, setImageUrl] = useState('')
  const [stampType, setStampType] = useState(cardProfile?.stamp_type || 'value')
  const [label, setLabel] = useState<string>('Stamps')
  const [frequencyLimitEnabled, setFrequencyLimitEnabled] = useState(
    cardProfile?.frequency_limit_enabled
  )
  const [maxStampsEnabled, setMaxStampsEnabled] = useState(
    cardProfile?.max_stamps_enabled
  )
  const [showExclusive, setShowExclusive] = useState<boolean>(
    !!activeCard?.exclusive
  )
  const [
    queryCategoriesData,
    { data: categoriesData, loading: isLoadingCategoriesData }
  ] = useLazyQuery<{
    categories: Category[]
  }>(GET_ACTIVE_MERCHANT_CATEGORIES, {
    fetchPolicy: 'no-cache'
  })
  const { categories } = categoriesData ?? {}

  const [upsertLoyaltyCardRewards] = useMutation(UPSERT_LOYALTY_CARD_REWARDS, {
    fetchPolicy: 'no-cache'
  })
  const [archiveReward] = useMutation(ARCHIVE_REWARD, {
    fetchPolicy: 'no-cache'
  })
  const [unredeemCustomerCards] = useMutation(UNREDEEM_CUSTOMER_LOYALTY_CARDS, {
    fetchPolicy: 'no-cache'
  })
  const [uploadLoyaltyCardImage] = useMutation(UPLOAD_LOYALTY_CARD_IMAGE, {
    fetchPolicy: 'no-cache'
  })
  const [setCustomerRewardsExpiration] = useMutation(
    SET_CUSTOMER_REWARDS_EXPIRATION,
    {
      fetchPolicy: 'no-cache'
    }
  )
  const [bulkAddCustomers] = useMutation(BULK_ADD_CUSTOMERS_TO_EXCLUSIVE_CARD)
  const [generateStampQR] = useMutation(GENERATE_STAMP_QR_TOKEN, {
    fetchPolicy: 'no-cache'
  })

  const hasLoyaltyCardErrors = (error) => {
    const requiredCardFields = [
      'name',
      'stamps_per_scan',
      'stamp_value',
      'starts_at',
      'ends_at',
      'image',
      'product_variant_ids',
      'stamp_type',
      'store_ids'
    ]

    const errorFields = error?.errorFields?.map((field) => field.name.join('.'))

    return intersection(errorFields, requiredCardFields).length > 0
  }

  const getCategoriesData = (shouldQuery: boolean) => {
    if (!shouldQuery) return

    queryCategoriesData({
      variables: {
        merchantId: user.merchant.id
      }
    })
  }

  const validateFields = () => {
    form
      .validateFields()
      .then(() => {})
      .catch((error) => {
        if (hasLoyaltyCardErrors(error)) {
          setActiveTab('loyalty-card')
        }

        if (error?.errorFields?.length > 0) {
          message.error(
            'Oops, something went wrong! Please check the errors below.',
            3
          )
        }
      })
  }

  const onFinish = (values: LoyaltyCardType & LoyaltyRewardType) => {
    setDisabledSubmit(true)
    const {
      name,
      description,
      starts_at,
      code,
      ends_at,
      stamps_per_scan,
      stamp_value,
      stamp_label,
      stamp_type,
      product_variant_ids,
      store_ids,
      exclusive,
      frequency_limit_enabled,
      interval,
      interval_unit,
      max_stamps_enabled,
      max_stamps,
      rewards
    } = values

    const formattedStoreIds = stringListForMutation(
      cardProfile?.store_ids,
      store_ids.filter((id) => id !== 'all')
    )

    form.validateFields().then(() => {
      const code_hashed = code ? code : codeHashed

      message.loading(
        `${loyaltyCardId ? 'Updating' : 'Creating'} Loyalty Card. Please wait.`,
        1
      )

      const startDate =
        starts_at && starts_at.isValid() && moment(starts_at).startOf('day')
      const endDate =
        ends_at && ends_at.isValid() && moment(ends_at).endOf('day')

      const rewards_value = !!rewards?.length
        ? rewards
        : rewardsProfile?.length > 0
        ? rewardsProfile
        : [rewardInitialValues]

      const updatedRewards = rewards_value.reduce((acc, reward) => {
        // remove product_limit_enabled and expiration_enabled in variables
        const {
          product_limit_enabled: enabled,
          expiration,
          expiration_unit,
          expiration_enabled,
          product_limit,
          minimum_value_fulfillments,
          ...rest
        } = reward

        return [
          ...acc,
          {
            ...rest,
            inserted_at: 'now()',
            loyalty_card_id: loyaltyCardId,
            updated_at: 'now()',
            product_limit: enabled ? product_limit : null,
            expiration: expiration_enabled ? expiration : null,
            expiration_unit: expiration_enabled ? expiration_unit : null,
            product_variant_ids: stringListForMutation(
              reward.product_variant_ids,
              reward.product_variant_ids
            ),
            minimum_value_fulfillments: stringListForMutation(
              minimum_value_fulfillments
            )
          }
        ]
      }, [])

      const variables = {
        id: loyaltyCardId,
        merchant_id: user.merchant.id,
        code: code_hashed,
        name: name,
        description: description,
        starts_at: startDate,
        ends_at: endDate,
        stamp_type: stamp_type,
        stamp_label: stamp_label,
        stamps_per_scan: stamps_per_scan,
        store_ids: formattedStoreIds,
        all_stores_enabled:
          store_ids.filter((id) => id !== 'all').length === stores.length,
        exclusive: !!exclusive,
        frequency_limit_enabled: frequency_limit_enabled,
        interval: interval,
        interval_unit: interval_unit,
        max_stamps_enabled: max_stamps_enabled,
        max_stamps: max_stamps,
        rewards: updatedRewards
      }

      const addedLoyaltyProductIds =
        stamp_type === 'value'
          ? {
              ...variables,
              stamp_value: stamp_value,
              product_variant_ids: stringListForMutation(
                cardProfile?.product_variant_ids,
                cardProfile?.product_variant_ids
              )
            }
          : {
              ...variables,
              product_variant_ids: stringListForMutation(
                product_variant_ids,
                product_variant_ids
              ),
              stamp_value: cardProfile?.stamp_value
            }

      upsertLoyaltyCardRewards({
        variables: addedLoyaltyProductIds
      })
        .catch((err) => {
          setDisabledSubmit(false)
          message.destroy()
          message.error(`Unable to ${action} loyalty card. ${err}`)
        })
        .then((result) => {
          const uploadPromise = image
            ? uploadLoyaltyCardImage({
                variables: {
                  loyalty_card_id: loyaltyCardId,
                  image: image
                }
              })
            : Promise.resolve('Skip upload')

          const existingRewardIds =
            rewardsProfile?.map((reward) => reward.id) || []
          const newRewardIds = rewards_value.map((reward) => reward.id)
          const hasNewReward = newRewardIds.reduce((acc, id) => {
            return !!!existingRewardIds.includes(id)
          }, false)

          const unredeemPromise = hasNewReward
            ? unredeemCustomerCards({
                variables: {
                  loyaltyCardId: loyaltyCardId
                }
              })
            : Promise.resolve('Skip unredeem')

          const customerRewardsExpPromise =
            result?.data?.rewards?.returning?.map((updatedReward) => {
              if (updatedReward.months_expiration) {
                return setCustomerRewardsExpiration({
                  variables: {
                    rewardId: updatedReward.id,
                    monthsExpiration: updatedReward.months_expiration
                  }
                })
              }
              return Promise.resolve(
                `Skip customer rewards expiration update for: ${updatedReward.id}`
              )
            })

          const addCustomersToExclusiveCard =
            !!exclusive &&
            customerIds.length > 0 &&
            customerIds.length !==
              activeCard?.exclusive_loyalty_customers.length
              ? bulkAddCustomers({
                  variables: {
                    cardId: loyaltyCardId,
                    customerIds,
                    merchantId: user.merchant.id,
                    userId: user.id
                  }
                })
                  .then((result: BulkAddResponse) => {
                    const bulk_add_customer =
                      result?.data?.bulkAddCustomersExclusiveLoyaltyCard
                    const process = bulk_add_customer?.includes('Adding')
                      ? 'Adding'
                      : 'Removing'
                    const stamp_log_id = bulk_add_customer?.replace(
                      `${process} customers to exclusive card on process: `,
                      ''
                    )

                    message.loading({
                      key: stamp_log_id,
                      duration: 1,
                      content:
                        'Your requested loyalty card changes are in progress'
                    })
                    message.info(`Please refresh the page`, 5)
                    localStorage.setItem('exclusive', 'true')
                    localStorage.setItem('stamp_log_id', stamp_log_id!)
                  })
                  .catch((error) =>
                    message.error(
                      `Unable to add customers to exclusive card due to: ${error}`,
                      5
                    )
                  )
              : Promise.resolve('Skip adding customers')

          const generateToken =
            action === 'create'
              ? generateStampQR({
                  variables: {
                    loyaltyCardId: loyaltyCardId,
                    secondsExpiration: null
                  }
                })
              : Promise.resolve('Skipping token generation')

          return Promise.allSettled([
            uploadPromise,
            unredeemPromise,
            addCustomersToExclusiveCard,
            generateToken,
            ...customerRewardsExpPromise
          ])
        })
        .then(() => {})
        .finally(() => {
          setDisabledSubmit(false)
          client.resetStore()
          setImageUploadLoading(false)
          setImageUrl(
            `https://${user.merchant.slug}.${
              env.ASSET_HOST
            }/assets/loyalty_card/${
              cardProfile?.id
            }?size=original&${moment().unix()}`
          )

          if (
            !showExclusive ||
            customerIds.length === 0 ||
            customerIds.length ===
              activeCard?.exclusive_loyalty_customers.map(
                (ex: { customer: User }) => ex.customer.id
              ).length
          ) {
            message.success(`Loyalty card ${action}d`)

            onDrawerClose()
          } else {
            window.location.reload()
          }
        })
    })
  }

  const handleRewardsRemove = (remove, rewardId, fieldName) => {
    const existingRewardIds = rewardsProfile.map((reward) => reward.id)
    message.loading('Removing reward. Please wait...')
    if (existingRewardIds.includes(rewardId)) {
      archiveReward({ variables: { id: rewardId } })
        .then((result) => {
          message.destroy()
          message.info('Reward has been removed!')
          remove(fieldName)
        })
        .catch((err) => {
          message.destroy()
          message.error('Unable to remove reward.')
          console.error(err)
        })
    } else {
      remove(fieldName)
      message.destroy()
      message.info('Reward has been removed!')
    }
  }

  const handleRewardTypeChange = (value: 'fixed' | 'percentage') => {
    form.setFieldsValue({
      reward_value_type: value
    })
  }

  useEffect(() => {
    setImageUrl(
      `https://${user.merchant.slug}.${env.ASSET_HOST}/assets/loyalty_card/${
        cardProfile?.id
      }?size=original&${moment().unix()}`
    )

    return () => {
      setDisabledSubmit(false)
      setImageUrl('')
    }
  }, [activeCard, activeRewards])

  useEffect(() => {
    getCategoriesData(stampType === 'product')
  }, [])

  useEffect(() => {
    setImage(undefined)
  }, [onDrawerClose])

  useEffect(() => {
    setLoyaltyCardId(cardProfile?.id || uuid())

    handleLabelChange(cardProfile?.stamp_label || 'stamps')
  }, [cardProfile?.id])

  const beforeImageUpload = (file: File) => {
    setImage(file)

    // fallback to remove form warning for image still showing even after adding an image
    form.setFields([{ name: 'image', errors: [] }])
    setImageUploadLoading(false)
    return true // this always expects a boolean return value
  }

  const renderDisplayImage = () => {
    if (image && !imageUploadLoading) {
      return (
        <img
          className='_h-100 _w-100'
          src={URL.createObjectURL(image)}
          alt='loyalty card'
        />
      )
    } else if (!imageUploadLoading && cardProfile && imageUrl) {
      return <Avatar size={150} shape='square' src={imageUrl} />
    } else if (imageUploadLoading) {
      return <LoadingOutlined />
    } else {
      return <FileImageOutlined />
    }
  }

  const rewardInitialValues = {
    id: uuid(),
    required_quantity: 1,
    reward_value_type: 'fixed',
    reward_value: 0,
    reward_type: 'value',
    variants_only: true
  }

  const validateFormTabs = (key: string) => {
    form
      .validateFields()
      .then(() => setActiveTab(key))
      .catch((error) => {
        if (!hasLoyaltyCardErrors(error)) {
          setActiveTab(key)
        } else {
          message.error(
            'Oops, something went wrong! Please check the errors below.',
            3
          )
        }
      })
  }

  const handleLabelChange = (value: string) => {
    if (value === 'stamps') {
      setLabel('Stamps')
    } else {
      setLabel('Points')
    }
  }

  return (
    <>
      <Form
        className='_pl-4'
        form={form}
        data-testid='loyalty-form'
        onFinish={onFinish}
        layout='vertical'
        labelCol={{ span: 20 }}
        wrapperCol={{ span: 20 }}
        scrollToFirstError={true}
        initialValues={{
          ...cardProfile,
          starts_at: moment.utc(cardProfile?.starts_at).tz('Europe/London'),
          ends_at: cardProfile?.ends_at
            ? moment.utc(cardProfile?.ends_at).tz('Europe/London')
            : null,
          stamp_type: cardProfile?.stamp_type || 'value',
          product_variant_ids: cardProfile?.product_variant_ids || [],
          rewards:
            rewardsProfile?.length > 0 ? rewardsProfile : [rewardInitialValues],
          stamps_per_scan: cardProfile ? cardProfile.stamps_per_scan : 1,
          stamp_label: cardProfile ? cardProfile.stamp_label : 'stamps',
          reward_value_type: 'fixed',
          reward_type: 'value',
          merchant_id: user?.merchant?.id
        }}
      >
        <Tabs
          activeKey={activeTab}
          onTabClick={(key) => {
            switch (key) {
              case 'rewards':
                validateFormTabs('rewards')
                break

              case 'apply-exclusive':
                validateFormTabs('apply-exclusive')
                break

              default:
                setActiveTab(key)
                break
            }
          }}
        >
          <TabPane key='loyalty-card' tab='Card details'>
            <Form.Item name='code' style={{ display: 'none' }}>
              <Input type='hidden' />
            </Form.Item>
            <Form.Item
              name='name'
              label={
                <>
                  <span>Card name</span>
                  <Tooltip
                    title={
                      'This is the name that will appear on your loyalty card, it is best to be descriptive: eg: Spend £50 get 10% off your next order.'
                    }
                  >
                    <QuestionCircleOutlined className='_ml-8 _mr-8' />
                  </Tooltip>
                </>
              }
              rules={[...requiredRule, ...uniqueName]}
            >
              <Input type='text' required />
            </Form.Item>
            <Form.Item name='description' label='Card Description'>
              <TextArea />
            </Form.Item>
            <Row className='_mb-0'>
              <Col span={6}>
                <Form.Item
                  name='image'
                  label={
                    <>
                      <span>Upload image</span>
                      <Tooltip
                        title={
                          'This image will appear at the top of your loyalty card.'
                        }
                      >
                        <QuestionCircleOutlined className='_ml-8 _mr-8' />
                      </Tooltip>
                    </>
                  }
                  rules={image || cardProfile?.image ? [] : requiredImageRule}
                >
                  <Upload
                    name='image'
                    accept='.png, .jpg, .jpeg'
                    fileList={image ? [image] : []}
                    onRemove={() => setImage(undefined)}
                    listType='picture-card'
                    className='avatar-uploader -square'
                    showUploadList={false}
                    beforeUpload={beforeImageUpload}
                  >
                    {renderDisplayImage()}
                  </Upload>
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <span
                style={{
                  marginTop: 20,
                  fontFamily: 'Poppins',
                  fontWeight: 700,
                  fontSize: '18px'
                }}
              >
                Card Settings
              </span>
            </Row>
            <Form.Item>
              <Row gutter={8} className='_mb-0'>
                <Col span={12}>
                  <Form.Item
                    name='starts_at'
                    label={
                      <>
                        <span>Duration</span>
                        <Tooltip
                          title={
                            'This is the date your loyalty offer starts. No stamps can be collected before this date. You will need to publish your card to make sure it goes live on this date'
                          }
                        >
                          <QuestionCircleOutlined className='_ml-8 _mr-8' />
                        </Tooltip>
                      </>
                    }
                    rules={[...startDateRule, ...requiredRule]}
                    className='_mb-0'
                  >
                    <DatePicker
                      format='DD-MM-YYYY'
                      placeholder='Start At'
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name='ends_at' label={<></>} className='_mb-0'>
                    <DatePicker
                      format='DD-MM-YYYY'
                      placeholder='No Expiry Date'
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Form.Item>
            <LoyaltyStoreSelect
              form={form}
              stores={stores}
              defaultValue={
                cardProfile?.all_stores_enabled
                  ? [...stores.map((s) => s.id), 'all']
                  : cardProfile?.store_ids || []
              }
            />
            <Form.Item
              name='stamp_label'
              label={
                <>
                  <span>Stamp or Points</span>
                  <Tooltip
                    title={
                      'Choose if your loyalty card uses a stamp based design or a point based design.'
                    }
                  >
                    <QuestionCircleOutlined className='_ml-8 _mr-8' />
                  </Tooltip>
                </>
              }
              rules={requiredRule}
            >
              <Radio.Group
                data-testid='stampLabel'
                onChange={(e) => handleLabelChange(e.target.value)}
              >
                <Space direction='vertical' align='start'>
                  <Radio value='stamps'>Stamp</Radio>
                  <Radio value='points'>Points</Radio>
                </Space>
              </Radio.Group>
            </Form.Item>
            <Form.Item
              name='stamp_type'
              label={`Associating ${label}`}
              rules={requiredRule}
            >
              <Radio.Group
                data-testid='associatingStamps'
                onChange={(e) => {
                  setStampType(e.target.value)
                  getCategoriesData(e.target.value === 'product')
                }}
              >
                <Space direction='vertical' align='start'>
                  <Radio value='value'>
                    <Row align='middle' className='_mb-0'>
                      <span>£ equivalent</span>
                      <Tooltip
                        title={
                          'Earn stamps or points based on the amount of money spent, eg: 1 point for each £1 spent.'
                        }
                      >
                        <QuestionCircleOutlined className='_ml-8 _mr-8' />
                      </Tooltip>
                      {stampType === 'value' && (
                        <div style={{ position: 'absolute', height: '5px' }}>
                          <Form.Item
                            name='stamp_value'
                            rules={[...nonZeroRule, ...nonNegativeRule]}
                            style={{
                              position: 'relative',
                              left: 120,
                              top: -16,
                              marginBottom: 0,
                              marginLeft: 10
                            }}
                          >
                            <Input
                              type='number'
                              style={{ width: 150 }}
                              addonAfter={<PoundOutlined />}
                              min={0.01}
                              step={0.01}
                            />
                          </Form.Item>
                        </div>
                      )}
                    </Row>
                  </Radio>
                  <Radio value='product'>
                    <Row align='middle' className='_mb-0'>
                      <span>Product/s</span>
                      <Tooltip
                        title={
                          'Earn stamps or points based on products purchased eg: 1 stamp for each coffee purchased.'
                        }
                      >
                        <QuestionCircleOutlined className='_ml-8 _mr-8' />
                      </Tooltip>
                    </Row>
                  </Radio>
                </Space>
              </Radio.Group>
            </Form.Item>
            {stampType === 'product' && (
              <Form.Item
                name='product_variant_ids'
                label={
                  <Row justify='space-between' style={{ width: '100%' }}>
                    <Col>Choose product/s</Col>
                  </Row>
                }
                rules={reqProductIdsRule}
                className='loyalty-product-label'
                required
              >
                <ProductPanels
                  categories={categories}
                  selectedProductVariantIds={
                    cardProfile?.product_variant_ids ?? []
                  }
                  form={form}
                  formType='loyalty'
                  isLoadingCategoriesData={isLoadingCategoriesData}
                />
              </Form.Item>
            )}
            <Row>
              <span
                style={{
                  marginTop: 20,
                  fontFamily: 'Poppins',
                  fontWeight: 700,
                  fontSize: '18px'
                }}
              >
                In-store QR scan settings
              </span>
            </Row>
            <Form.Item
              label={
                <>
                  <span>{`${label} Frequency`}</span>
                  <Tooltip
                    title={`Set a limit for how often a user can ${label.toLocaleLowerCase()} a card.`}
                  >
                    <QuestionCircleOutlined className='_ml-8 _mr-8' />
                  </Tooltip>
                </>
              }
              name='frequency_limit_enabled'
            >
              <Switch
                checked={frequencyLimitEnabled}
                onChange={(value) => setFrequencyLimitEnabled(value)}
                className='_ml-8'
              />
            </Form.Item>
            {frequencyLimitEnabled && (
              <>
                <Form.Item
                  name='interval'
                  label='Interval'
                  rules={requiredRule}
                >
                  <TextFieldNumber
                    inputType='number'
                    min={1}
                    step={1}
                    style={{ maxWidth: '160px' }}
                  />
                </Form.Item>
                <Form.Item
                  name='interval_unit'
                  label='Interval Unit'
                  rules={requiredRule}
                >
                  <Radio.Group data-testid='intervalUnit'>
                    <Space direction='vertical' align='start'>
                      <Radio value='minutes'>Minutes</Radio>
                      <Radio value='hours'>Hours</Radio>
                    </Space>
                  </Radio.Group>
                </Form.Item>
                <Form.Item
                  label={
                    <>
                      <span>Max Stamps Enabled?</span>
                      <Tooltip
                        title={
                          'Set a limit for how many stamps a user can receive every interval.'
                        }
                      >
                        <QuestionCircleOutlined className='_ml-8 _mr-8' />
                      </Tooltip>
                    </>
                  }
                  name='max_stamps_enabled'
                >
                  <Switch
                    checked={maxStampsEnabled}
                    onChange={(value) => setMaxStampsEnabled(value)}
                    className='_ml-8'
                  />
                </Form.Item>
                {maxStampsEnabled && (
                  <Form.Item
                    name='max_stamps'
                    label='Max Stamps'
                    rules={requiredRule}
                  >
                    <TextFieldNumber
                      inputType='number'
                      min={0}
                      step={1}
                      style={{ maxWidth: '160px' }}
                    />
                  </Form.Item>
                )}
              </>
            )}
            <Form.Item
              name='stamps_per_scan'
              label={
                <>
                  <span>{label} per scan</span>
                  <Tooltip
                    title={
                      'This is the default number of stamps given when a QR code is scanned for a purchase made outside of Slerp by a customer. Typically it is set at 1.'
                    }
                  >
                    <QuestionCircleOutlined className='_ml-8 _mr-8' />
                  </Tooltip>
                </>
              }
              rules={requiredRule}
            >
              <TextFieldNumber
                inputType='number'
                min={1}
                max={99}
                step={1}
                style={{ maxWidth: '160px' }}
              />
            </Form.Item>
            <Row className='_mt-40' gutter={[16, 0]}>
              <Col>
                <Button onClick={onDrawerClose} type='ghost'>
                  <Link to='#'>Cancel</Link>
                </Button>
              </Col>
              <Col>
                {!!activeCard ? (
                  <Button
                    title='Save'
                    type='primary'
                    htmlType='submit'
                    onClick={() => {
                      form
                        .validateFields()
                        .then(() => {})
                        .catch((error) => {
                          if (hasLoyaltyCardErrors(error)) {
                            setActiveTab('loyalty-card')
                          }

                          if (error?.errorFields?.length > 0) {
                            message.error(
                              'Oops, something went wrong! Please check the errors below.',
                              3
                            )
                          }
                        })
                    }}
                    disabled={disabledSubmit}
                  >
                    Save Card
                  </Button>
                ) : (
                  <Button
                    title='Save'
                    type='primary'
                    htmlType='submit'
                    onClick={() => {
                      form
                        .validateFields()
                        .then(() => setActiveTab('rewards'))
                        .catch((error) => {
                          if (!hasLoyaltyCardErrors(error)) {
                            setActiveTab('loyalty-card')
                          } else {
                            message.error(
                              'Oops, something went wrong! Please check the errors below.',
                              3
                            )
                          }
                        })
                    }}
                  >
                    Save
                  </Button>
                )}
              </Col>
            </Row>
          </TabPane>
          <TabPane key='rewards' tab='Reward details'>
            <RewardsForm
              loyaltyCardId={loyaltyCardId}
              categories={categories}
              onRemove={handleRewardsRemove}
              handleRewardTypeChange={handleRewardTypeChange}
              form={form}
              isLoadingCategoriesData={isLoadingCategoriesData}
              getCategoriesData={getCategoriesData}
              shouldQueryOnRewards={shouldQueryOnRewards}
            />
            <Form.Item>
              <Row className='_mt-40' gutter={[16, 0]}>
                <Col>
                  <Button onClick={onDrawerClose} type='ghost'>
                    <Link to='#'>Cancel</Link>
                  </Button>
                </Col>
                <Col>
                  {!!activeCard ? (
                    <Button
                      title='Save'
                      type='primary'
                      htmlType='submit'
                      onClick={() => {
                        form
                          .validateFields()
                          .then(() => {})
                          .catch((error) => {
                            if (hasLoyaltyCardErrors(error)) {
                              setActiveTab('rewards')
                            }

                            if (error?.errorFields?.length > 0) {
                              message.error(
                                'Oops, something went wrong! Please check the errors below.',
                                3
                              )
                            }
                          })
                      }}
                      disabled={disabledSubmit}
                    >
                      Save Card
                    </Button>
                  ) : (
                    <Button
                      title='Save'
                      type='primary'
                      onClick={() => {
                        form
                          .validateFields()
                          .then(() => setActiveTab('apply-exclusive'))
                          .catch((error) => {
                            if (!hasLoyaltyCardErrors(error)) {
                              setActiveTab('rewards')
                            } else {
                              message.error(
                                'Oops, something went wrong! Please check the errors below.',
                                3
                              )
                            }
                          })
                      }}
                    >
                      Save
                    </Button>
                  )}
                </Col>
              </Row>
            </Form.Item>
          </TabPane>
          <TabPane key='apply-exclusive' tab='Exclusive Functionality'>
            <Form.Item
              label='Make this card exclusive?'
              name='exclusive'
              valuePropName='checked'
            >
              <Switch
                className='_ml-8'
                onChange={() => setShowExclusive((prev) => !prev)}
              />
            </Form.Item>
            {showExclusive && (
              <ExclusiveForm
                setCustomerIds={setCustomerIds}
                customers={activeCard?.exclusive_loyalty_customers.map(
                  (ex: { customer: User }) => ex.customer
                )}
              />
            )}
            <Form.Item>
              <Row className='_mt-40' gutter={[16, 0]}>
                <Col>
                  <Button onClick={onDrawerClose} type='ghost'>
                    <Link to='#'>Cancel</Link>
                  </Button>
                </Col>
                <Col>
                  <Button
                    title='Save'
                    type='primary'
                    htmlType='submit'
                    onClick={validateFields}
                    disabled={disabledSubmit}
                  >
                    Save Card
                  </Button>
                </Col>
              </Row>
            </Form.Item>
          </TabPane>
        </Tabs>
      </Form>
    </>
  )
}

export default LoyaltyCardForm
